/* eslint-disable @typescript-eslint/no-explicit-any */
import { DOCUMENT } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, signal, Renderer2, computed } from '@angular/core';
import {
  RouterOutlet,
  Router,
  NavigationEnd,
  ActivatedRoute,
  ActivatedRouteSnapshot,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { SidebarStateService } from '../../../../data-access/src/lib/sidebar-state.service';
import { SidebarWrapperComponent } from '../sidebar-wrapper/sidebar-wrapper.component';
import {
  AuthService,
  TranslationService,
  ThemeService,
  leerContextoDeProducto,
  UiProfileService,
} from '@nebula-shared/data-access';
import { TopbarWrapperComponent } from '../topbar-wrapper/topbar-wrapper.component';
import { BreadcrumbWrapperComponent } from '../breadcrumb-wrapper/breadcrumb-wrapper.component';
import { FooterWrapperComponent } from '../footer-wrapper/footer-wrapper.component';
import { NebulinaComponent } from '../nebulina/nebulina.component';
import { ContextoSesionComponent } from '../contexto-sesion/contexto-sesion.component';
import { BreadcrumbItem } from '../../../../domain/src/lib/breadcrumb.model';
import { PROFILE_MENU } from '../../../../domain/src/lib/layout.models';
import { PROFILE_MENU_ITEMS } from '../../../../const/src/lib/layout.constants';
import { BreakpointObserver } from '@angular/cdk/layout';
import { RouteSyncService } from '../../../../utils/src/lib/route-sync';
import { NebulaNavigationService } from 'nebula-ui-kit';
import { installOpenTabLimit } from '../../../../utils/src/lib/tab-limit-nav';
import { MODULOS_EN_CONSTRUCCION } from '../../../../const/src/lib/modules.constant';

/** Tramo en que el menu abierto flota sobre el contenido en lugar de empujarlo. */
const PANTALLA_ANGOSTA = '(max-width: 767px)';

@Component({
  selector: 'nebula-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: true,
  imports: [
    RouterOutlet,
    SidebarWrapperComponent,
    TopbarWrapperComponent,
    BreadcrumbWrapperComponent,
    FooterWrapperComponent,
    NebulinaComponent,
    ContextoSesionComponent,
  ],
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly tema = inject(ThemeService);

  private readonly authService = inject(AuthService);
  private readonly translation = inject(TranslationService);
  private readonly document = inject(DOCUMENT);
  readonly renderer = inject(Renderer2);
  readonly breakpointObserver = inject(BreakpointObserver);
  private readonly navService = inject(NebulaNavigationService);
  private readonly perfilVisual = inject(UiProfileService);

  showSidebar = false;
  showHeader = false;
  showBreadcrumb = false;
  /** La ruta es del area de trabajo: no es la seleccion de empresa ni de producto. */
  private nebulinaEnRuta = false;
  isSidebarCollapsed = false;

  /** La pantalla esta por debajo de 768px: el menu abierto flota sobre el contenido. */
  pantallaAngosta = false;

  /** El menu lo contrajo el tramo angosto, no el usuario: se reabre al volver a pantalla ancha. */
  private menuContraidoPorAngosta = false;
  /**
   * Si la aplicacion se ve oscura.
   *
   * Lo decide el dueño del tema y no este armazon: la misma decision estaba tomada
   * aqui, en la pantalla de entrada y en el pie de carga, cada una leyendo el
   * almacenamiento por su cuenta. Con tres copias, un cambio de turno repintaba
   * unas partes y otras no.
   */
  readonly isDarkMode = this.tema.oscuro;
  userName = signal<string>('');
  readonly subs = new Subscription();

  readonly sidebarState = inject(SidebarStateService);
  readonly router = inject(Router);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly routeSync = inject(RouteSyncService);
  showSearch = signal<boolean>(false);
  showNotifications = signal<boolean>(false);
  showRefresh = signal<boolean>(false);
  showProducts = signal<boolean>(true);
  showSystemDate = signal<boolean>(false);
  breadcrumbItems = signal<BreadcrumbItem[]>([]);

  itemsProfileMenu = signal<Array<PROFILE_MENU>>(PROFILE_MENU_ITEMS);

  ngOnInit(): void {
    installOpenTabLimit(this.navService);

    this.updateLayoutFromRoute();
    this.updateTopbarElementsFromRoute();
    this.updateSystemDateVisibility();
    this.updateBreadcrumbs();
    this.updateDocumentTitle();

    this.updateProfileMenu();

    window.addEventListener('storage', this.handleStorageChange.bind(this));

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateLayoutFromRoute();
      this.updateTopbarElementsFromRoute();
      this.updateSystemDateVisibility();
      this.updateBreadcrumbs();
      this.updateDocumentTitle();

      this.updateProfileMenu();
    });

    this.subs.add(
      this.sidebarState.isExpanded$.subscribe((expanded) => {
        this.isSidebarCollapsed = !expanded;
      }),
    );

    // En pantalla angosta el menu no puede quedarse abierto tapando el trabajo: al entrar en ese
    // tramo se contrae a su barra de iconos, y el usuario lo abre cuando lo necesita.
    this.subs.add(
      this.breakpointObserver.observe(PANTALLA_ANGOSTA).subscribe(({ matches }) => {
        this.pantallaAngosta = matches;
        if (matches && this.showSidebar && !this.isSidebarCollapsed) {
          this.menuContraidoPorAngosta = true;
          this.onSidebarCollapsedChange(true);
        } else if (!matches && this.menuContraidoPorAngosta) {
          // Si fue el tramo angosto quien lo contrajo, al volver a pantalla ancha se reabre.
          this.menuContraidoPorAngosta = false;
          this.onSidebarCollapsedChange(false);
        }
      }),
    );

    this.tema.aplicar();
    this.userName.set(this.user());
  }

  private updateProfileMenu(): void {
    this.itemsProfileMenu.set(PROFILE_MENU_ITEMS);
  }

  private handleStorageChange(event: StorageEvent): void {
    if (event.key === 'hasMultipleModules' || event.key === 'modulesCount') {
      this.updateProfileMenu();
    }
  }

  onMenuItemClick(action: string): void {
    const currentUrl = this.router.url;
    const isRestrictedRoute = this.isRestrictedRoute(currentUrl);

    if (action === 'Seleccionar Productos') {
      if (!isRestrictedRoute) {
        this.router.navigate(['/layout/products-selection']);
      }
    } else if (action === 'Configuración') {
      this.router.navigate(['/layout/settings']);
    } else if (action === 'Cerrar sesión') {
      this.logout();
    }
  }

  private updateLayoutFromRoute(): void {
    let route = this.activatedRoute.snapshot.firstChild;

    while (route?.firstChild) {
      route = route.firstChild;
    }

    const currentUrl = this.router.url;
    const isRestrictedRoute = this.isRestrictedRoute(currentUrl);
    this.nebulinaEnRuta = !isRestrictedRoute;

    if (route?.data) {
      this.showSidebar = route.data['showSidebar'] ?? true;
      this.showHeader = route.data['showHeader'] ?? true;

      if (isRestrictedRoute) {
        this.showBreadcrumb = false;
      } else {
        this.showBreadcrumb = route.data['showBreadcrumb'] ?? true;
      }
    } else {
      this.showSidebar = true;
      this.showHeader = true;
      this.showBreadcrumb = !isRestrictedRoute;
    }
  }

  private updateTopbarElementsFromRoute(): void {
    let route = this.activatedRoute.snapshot.firstChild;

    while (route?.firstChild) {
      route = route.firstChild;
    }

    const currentUrl = this.router.url;

    this.showProducts.set(!this.isRestrictedRoute(currentUrl));

    if (route?.data) {
      this.showSearch.set(route.data['showSearch'] ?? this.shouldShowSearchByUrl(currentUrl));
      this.showNotifications.set(
        route.data['showNotifications'] ?? this.shouldShowNotificationsByUrl(currentUrl),
      );
      this.showRefresh.set(route.data['showRefresh'] ?? this.shouldShowRefreshByUrl(currentUrl));
      this.showSystemDate.set(
        route.data['showSystemDate'] ?? this.shouldShowSystemDateByUrl(currentUrl),
      );
    } else {
      this.showSearch.set(this.shouldShowSearchByUrl(currentUrl));
      this.showNotifications.set(this.shouldShowNotificationsByUrl(currentUrl));
      this.showRefresh.set(this.shouldShowRefreshByUrl(currentUrl));
      this.showSystemDate.set(this.shouldShowSystemDateByUrl(currentUrl));
    }
  }

  private shouldShowSearchByUrl(url: string): boolean {
    return !this.isRestrictedRoute(url);
  }

  private shouldShowNotificationsByUrl(url: string): boolean {
    return !this.isRestrictedRoute(url);
  }

  private shouldShowRefreshByUrl(url: string): boolean {
    return !this.isRestrictedRoute(url);
  }

  private shouldShowSystemDateByUrl(url: string): boolean {
    return (
      url.includes('/layout/accounting') ||
      url.includes('layout/tesorería') ||
      url.includes('layout/tesorer%C3%ADa') ||
      url.includes('layout/invoicing') ||
      url.includes('layout/budget')
    );
  }

  private updateSystemDateVisibility(): void {
    const currentUrl = this.router.url;
    const shouldShowSystemDate = this.shouldShowSystemDateByUrl(currentUrl);
    this.showSystemDate.set(shouldShowSystemDate);
  }

  /**
   * Rutas restringidas donde NO se debe mostrar sidebar, breadcrumb, búsqueda, etc.
   * Se actualiza para incluir invoicing si es necesario.
   * Si invoicing DEBE mostrar sidebar, entonces NO debe estar en esta lista.
   */
  private isRestrictedRoute(url: string): boolean {
    const restrictedRoutes = [
      '/layout/company',
      '/layout/products',
      // Si invoicing NO debe mostrar sidebar, descomenta la línea de abajo:
      // '/layout/invoicing'
    ];
    return restrictedRoutes.some((route) => url.includes(route));
  }

  private updateDocumentTitle(): void {
    let route = this.activatedRoute.snapshot.firstChild;

    while (route?.firstChild) {
      route = route.firstChild;
    }

    const title = route?.title;
    if (typeof title === 'string') {
      this.document.title = this.translateRouteLabel(title);
    }
  }

  private translateRouteLabel(label: string): string {
    const translated = this.translation.translate(label);
    return translated === label ? label : translated;
  }

  private updateBreadcrumbs(): void {
    const currentUrl = this.router.url;

    const tabContainerModules = [
      'invoicing',
      'accounting',
      'tesorería',
      'budget',
      'vault',
      ...MODULOS_EN_CONSTRUCCION,
    ];
    const isTabContainerModule = tabContainerModules.some((module) =>
      currentUrl.includes(`/layout/${module}`),
    );

    if (isTabContainerModule) {
      this.breadcrumbItems.set([]);
      return;
    }

    const breadcrumbs: BreadcrumbItem[] = [];

    const collectRoutes = (route: ActivatedRouteSnapshot, accumulatedSegments: string[] = []) => {
      if (route.routeConfig?.path) {
        const path = route.routeConfig.path;

        if (path !== '' && path !== 'layout') {
          const newSegments = [...accumulatedSegments, path];
          const newUrl = `/${newSegments.join('/')}`;

          const breadcrumbData = route.data?.['breadcrumb'];

          if (breadcrumbData) {
            const rawLabel =
              typeof breadcrumbData === 'string' ? breadcrumbData : breadcrumbData.label;
            const label = this.translateRouteLabel(rawLabel);
            const icon = typeof breadcrumbData === 'object' ? breadcrumbData.icon : undefined;

            breadcrumbs.push({
              label: label,
              url: newUrl,
              icon: icon || 'Folder',
            });
          } else if (path !== 'list') {
            breadcrumbs.push({
              label: this.formatRouteLabel(path),
              url: newUrl,
              icon: 'Folder',
            });
          }

          if (route.firstChild) {
            collectRoutes(route.firstChild, newSegments);
          }
        } else if (route.firstChild) {
          collectRoutes(route.firstChild, accumulatedSegments);
        }
      } else if (route.firstChild) {
        collectRoutes(route.firstChild, accumulatedSegments);
      }
    };

    collectRoutes(this.activatedRoute.snapshot.root, []);

    const breadcrumbsWithLayout = breadcrumbs.map((item) => ({
      ...item,
      url: `/layout${item.url}`,
    }));

    const uniqueBreadcrumbs = breadcrumbsWithLayout.filter(
      (item, index, self) => index === self.findIndex((t) => t.url === item.url),
    );

    this.breadcrumbItems.set(uniqueBreadcrumbs);
  }

  onBreadcrumbNavigate(item: any): void {
    this.router.navigateByUrl(item.url).then();
  }

  private formatRouteLabel(routePath: string): string {
    return routePath
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /** Tocar el velo del menu abierto en pantalla angosta lo contrae y devuelve el trabajo. */
  cerrarMenuFlotante(): void {
    this.menuContraidoPorAngosta = true;
    this.onSidebarCollapsedChange(true);
  }

  onSidebarCollapsedChange(collapsed: boolean): void {
    this.isSidebarCollapsed = collapsed;
    this.sidebarState.setState(!collapsed, this.showSidebar);
  }

  get isConnected(): boolean {
    if (typeof window === 'undefined') return false;
    const userData = localStorage.getItem('user_data');
    return userData !== null && userData !== undefined && userData !== '';
  }

  darkmodeChange() {
    this.tema.alternar();
  }

  logout() {
    this.authService.logout();
  }

  private user(): string {
    const DATA_USER = localStorage.getItem('user_data');
    if (!DATA_USER) return '';
    const JSON_DATA_USER = JSON.parse(DATA_USER);
    return JSON_DATA_USER.username;
  }

  /** Perfil visual 3.0: la dependencia y la fecha van en la barra de la miga, no en la cabecera. */
  esPerfil30(): boolean {
    return this.perfilVisual.esPerfil30();
  }

  /**
   * Nebulina y su reserva salen solo en el area de trabajo y en el perfil 3.0 (decision de Carlos del
   * 22-oct-2026, opcion «A», tablero de sesiones). Se pregunta al perfil en cada pintado, como la
   * barra de la miga, asi que un cambio de perfil sin recargar tambien la quita o la pone.
   */
  get showNebulina(): boolean {
    return this.nebulinaEnRuta && this.esPerfil30();
  }

  get footerVariant(): 'default' | 'login' {
    return this.router.url.includes('/login') ? 'login' : 'default';
  }

  isTablet = computed(() =>
    this.breakpointObserver.isMatched('(min-width: 768px) and (max-width: 1024px)'),
  );

  logoUrl = computed(() => {
    const selectedRoute = leerContextoDeProducto('selectedRoute');
    const isVault = selectedRoute === 'vault';
    const isDark = this.isDarkMode();
    const isTablet = this.isTablet();

    if (isVault) {
      const vaultBasePath = isDark
        ? 'assets/shell/logos/dark/logo-nebula-vault-dark'
        : 'assets/shell/logos/light/logo-nebula-vault-light';

      const suffix = isTablet ? '-tablet' : '';
      const finalLogo = `${vaultBasePath}${suffix}.svg`;
      return finalLogo;
    }

    const defaultBasePath = isDark
      ? 'assets/shell/logos/dark/logo-nebula-dark'
      : 'assets/shell/logos/light/logo-nebula-light';

    const suffix = isTablet ? '-tablet' : '';
    const finalLogo = `${defaultBasePath}${suffix}.svg`;
    return finalLogo;
  });

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    window.removeEventListener('storage', this.handleStorageChange.bind(this));
  }
}
